import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useFormikContext } from 'formik';
import { hasUsableValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';
import { isBlankControlValue, toControlValue } from './calculatedControlValue';
import type { ControlOption } from './calculatedControlValue';
import { describeDiagnostic } from '@/utils/formula/formulaDiagnostics';
import { useOverriddenFields } from './overriddenFields';
import { useCalculatedValue } from './useCalculatedValue';
import type { CalculatedFieldState } from './calculatedField.interface';

// One entry of the rule's `blocked_by`. A `field_id` means the blocker is a row
// on this form, so the recruiter can fill it; without one it lives on another
// object — a requisition or job field — and no amount of filling in here will
// help.
export interface Blocker {
  label?: string;
  field_id?: string | number | null;
}

export interface CalculatedFieldSyncProps {
  // The form field the result is written to, so the field's own control shows
  // it — a calculated Yes/No is the Yes/No control, not a box of text.
  name: string;
  formula: string;
  fieldNameOf: (token: string) => string;
  // The backend's verdict on the saved rule. Omitted — nothing sends it yet —
  // means show whatever the server computes now.
  state?: CalculatedFieldState;
  // The fields a 'blocked' rule is still waiting on, named for the recruiter.
  blockedBy?: Blocker[];
  // The offer's own verdict at its last write. Where the live evaluation says
  // nothing — the server answers null with no diagnostics — this is the only
  // thing that can explain an empty field.
  savedDiagnostic?: { code?: string } | null;
  // Unlocked, the recruiter may type over the result, so it is theirs to keep.
  locked?: boolean;
  // The field's own error is suppressed on a locked field, so a required one
  // that comes back with nothing has to be said here or the save blocks in
  // silence (Venil, QA round 1).
  required?: boolean;
  subjectType?: string;
  kind?: string;
  fieldId?: string | number;
  // How the rule names this field — `f_<custom field id>` or `s_<token>`. What
  // the save reports as taken over, so it has to be the rule's spelling rather
  // than the form's.
  handle?: string;
  // The server's verdict from the last save: this offer's value is the
  // recruiter's, not the formula's. What the first look goes on where it is
  // given; undefined means the form has no verdict to offer and the box decides.
  overridden?: boolean;
  // The application the offer is for; related-object handles resolve from it.
  applicationId?: string | number;
  // A select's own options: the formula answers with their names, the control
  // holds their ids.
  options?: ControlOption[];
  multi?: boolean;
}

const quotedList = (names: string[]) =>
  names.length < 2
    ? `"${names[0]}"`
    : `${names
        .slice(0, -1)
        .map((name) => `"${name}"`)
        .join(', ')} and "${names[names.length - 1]}"`;

const blockedMessage = (names: string[]) =>
  `Fill in ${quotedList(names)} to see this value`;

const notHere = (names: string[]) =>
  `${quotedList(names)}, which ${names.length > 1 ? 'are' : 'is'} not on this offer form`;

// Nothing on this form will produce an off-form blocker, so the recruiter is
// told where the value has to come from rather than asked to fill it in — and
// a mixed list says both, or filling the fillable half leads nowhere.
const waitingMessage = (fillable: string[], offForm: string[]) => {
  if (fillable.length === 0) return `This value needs ${notHere(offForm)}`;
  if (offForm.length === 0) return blockedMessage(fillable);
  return `${blockedMessage(fillable)} — it also needs ${notHere(offForm)}`;
};

// The one diagnostic a recruiter can clear themselves: the engine says this
// when it has no value to work with. Every other code is the formula's own
// fault and no amount of filling in will help.
const isMissingInput = (diagnostic?: { code?: string } | null) =>
  diagnostic?.code === 'err_formula_not_computable';

const Refusal = ({ children }: { children: string }) => (
  // kula Text drops data-* attributes, so the hook sits on the wrapper.
  <Box mt="1" data-test-id="calculated-blocked">
    <Text variant="small" style={{ color: colors['pastel-1-dark'] }}>
      {children}
    </Text>
  </Box>
);

// Sits under a calculated field: works the value out, puts it where the
// field's own control reads it, and says why when there is none to show.
// It renders no control of its own — the kind's config already does.
// What a value looks like for the purpose of "is this still the formula's?".
//
// Loose on type, because the two sides disagree about it: the control holds a
// date as epoch milliseconds (a number) while the server stores every computed
// answer as `rendered.to_s` and the form seeds the box with that string
// verbatim. Compared with JSON.stringify, the echo of the formula's OWN answer
// read as something the recruiter typed on the save after every save — and the
// server now honours that and freezes the field (review of #7090).
const sameness = (value: unknown): string =>
  Array.isArray(value)
    ? value.map((one) => String(one ?? '')).join('\u0000')
    : String(value ?? '');

export const CalculatedFieldSync = ({
  name,
  formula,
  fieldNameOf,
  state,
  blockedBy,
  savedDiagnostic,
  locked = true,
  required = false,
  subjectType,
  kind,
  fieldId,
  handle,
  overridden: savedOverride,
  applicationId,
  options,
  multi,
}: CalculatedFieldSyncProps) => {
  const { values, setFieldValue, submitCount } =
    useFormikContext<Record<string, unknown>>();
  const takeovers = useOverriddenFields();
  const {
    value: computed,
    missing,
    inputs = [],
    diagnostic,
  } = useCalculatedValue({
    formula,
    values,
    fieldNameOf,
    subjectType,
    kind,
    fieldId,
    applicationId,
  });

  const named = (blockedBy ?? []).filter(
    (blocker): blocker is Blocker & { label: string } => Boolean(blocker?.label)
  );
  const onForm = named
    .filter(
      (blocker) => blocker.field_id !== null && blocker.field_id !== undefined
    )
    .map((blocker) => blocker.label);
  const offForm = named
    .filter(
      (blocker) => blocker.field_id === null || blocker.field_id === undefined
    )
    .map((blocker) => blocker.label);
  // `blocked_by` is the offer's verdict at its last write, so a field named
  // there may well have been filled since — and the message went on telling
  // the recruiter to fill a box they had already filled (Venil, round 2 #2).
  // The live reading says which inputs are still blank, so the saved list is
  // narrowed to those.
  const stillBlank = new Set(
    missing.map((field) => field.label).filter(Boolean)
  );
  // Only a name this reading knows about may be dropped: one it has never
  // heard of has not been loaded yet, so the saved verdict is all there is.
  const known = new Set(inputs.map((field) => field.label).filter(Boolean));
  const onFormWaiting = onForm.filter(
    (label) => !known.has(label) || stillBlank.has(label)
  );
  // The server said nothing, so the local engine's own reading of the formula
  // names what it could not read.
  const fillable =
    onForm.length > 0 || offForm.length > 0
      ? onFormWaiting
      : [missing[0]?.label].filter((label): label is string => Boolean(label));
  const waitingFor = fillable[0] ?? offForm[0];
  const blocked = state === 'blocked' || (!state && Boolean(waitingFor));
  // The server has just worked this one out, from what the form holds now.
  const live = computed !== null && computed !== undefined;
  // The saved verdict speaks only when nothing live does. Like `state`, it is
  // the offer's reading at its last write and goes stale the moment anyone
  // types — but without it a rule the server saved as `error` rendered an
  // empty box and said nothing at all, because the live evaluation answers
  // null with no diagnostics of its own (All Requisition Fields, demo-2).
  const refusal =
    diagnostic ?? (live ? undefined : (savedDiagnostic ?? undefined));
  // A 'blocked' or 'error' rule can still carry a value from an earlier save,
  // so the state decides whether a SAVED value may be shown at all.
  //
  // It cannot decide that for a live one. `state` is the offer's verdict from
  // its last write, so it is stale the moment anyone types — a field saved
  // blocked stayed blank however the recruiter filled the field it was waiting
  // on, even while evaluate_formula was answering with the right number on
  // every keystroke (CORE-5539). `hidden_by_condition` had the same problem and
  // the same answer (ENG-4212): re-read live, and let the saved flag speak only
  // when there is nothing live to speak for it.
  const usable =
    live ||
    hasUsableValue({
      state: blocked ? 'blocked' : (state ?? 'ok'),
      value: computed,
    });

  const shown = usable
    ? toControlValue(kind, computed, { options, multi })
    : { value: '' };
  // A result the field cannot hold is not written to a locked field: the
  // filler cannot correct it, so an empty box and the reason is all there is.
  // Unlocked, they can, so they are given the value to correct (CORE-5477).
  // A locked field with nothing to show holds `undefined`, not '': the payload
  // drops an undefined key, and '' is a value — an uncomputed formula field was
  // reaching the server as the recruiter clearing it.
  const nothingToStore = locked && (!usable || Boolean(shown.unstorable));
  const next = nothingToStore
    ? undefined
    : (shown.value ?? String(computed ?? ''));

  // A locked field publishes on every recompute — it is the formula's to own.
  //
  // An unlocked one is the recruiter's to override, but until they actually do
  // it is still a calculated field and has to follow its inputs. It used to be
  // seeded once and then left alone, which froze it at that first value: the
  // one field on the offer form that reads another formula sat stale however
  // the fields underneath it changed (Venil, #8a).
  //
  // So the test is whether the box still holds what this published, not
  // whether it has ever published. Anything else in there is the recruiter's —
  // typed now, or overridden on an earlier save — and from then on the field
  // is theirs and nothing here writes to it again (#8b).
  //
  // Every value this has published is remembered, not just the last one.
  // `setFieldValue` lands a render later, so a recompute that arrives in
  // between sees the previous published value still in the box — against the
  // latest alone that reads as the recruiter's and froze the field again, the
  // very thing #8a is about (review of #7078).
  const publishedRef = useRef<Set<string> | null>(null);
  // Seeded from the server: an offer saved with a take-over opens with the
  // field already theirs, so clearing the box is what hands it back.
  const overriddenRef = useRef(savedOverride === true);
  useEffect(() => {
    if (locked) {
      // Compared against undefined rather than '': the two serialise the same,
      // so a field that opened holding '' never got emptied out of the payload.
      // Kept ahead of the sameness test below for that reason -- that one reads
      // both as the empty string and would never fire the clear.
      if (next === undefined) {
        if (values[name] === undefined) return;
        setFieldValue(name, undefined);
        return;
      }
      if (sameness(values[name]) === sameness(next)) return;
      setFieldValue(name, next);
      return;
    }
    const held = sameness(values[name]);
    // Clearing the box is how a take-over is undone — there is no other way, and
    // without one a single mistyped number retires the formula for the life of
    // the offer. The empty value is remembered as published, or the next pass
    // reads the empty box itself as something the recruiter put there and takes
    // the field over again.
    //
    // Blank is the control's own answer, not '': an untouched single-select
    // holds the sentinel 0 (ENG-3456), and a cleared one goes back to it.
    if (overriddenRef.current) {
      if (!isBlankControlValue(values[name], kind)) return;
      overriddenRef.current = false;
      publishedRef.current = new Set([held]);
      // Reported, like the take-over: the computed value goes back in the box
      // below, so nothing the save carries still looks cleared.
      if (handle) takeovers?.unmark(handle);
      // Falls through to publish in this same pass rather than waiting for the
      // next one: clearing the box is the last thing that changes, so nothing
      // would re-run this effect and the field stayed empty.
    }
    if (!usable) return;
    // The first look goes on the server's verdict where there is one. Every
    // saved calculated field holds a value, so reading "not empty" as the
    // recruiter's reported every unlocked field on every reopened offer as taken
    // over — and the server, which now honours what it is told, froze the lot.
    //
    // Absent, the box decides, as it always has: the requisition and job forms
    // render this too and have no such verdict to offer, and a value they saved
    // is the only evidence those forms have. What counts as nothing there is the
    // control's own answer — an untouched single-select holds the sentinel 0
    // (ENG-3456), which read as a value the recruiter put there, so an unlocked
    // calculated dropdown decided it had been overridden on its first render and
    // never published at all.
    const theirs =
      publishedRef.current === null
        ? (savedOverride ?? !isBlankControlValue(values[name], kind))
        : !publishedRef.current.has(held);
    if (theirs) {
      overriddenRef.current = true;
      // Reported, not just remembered: the server keeps an override only for a
      // field the save names, and nothing else can tell it which those are.
      if (handle) takeovers?.mark(handle);
      return;
    }
    const nextValue = sameness(next);
    publishedRef.current = (publishedRef.current ?? new Set()).add(nextValue);
    if (held === nextValue) return;
    setFieldValue(name, next);
  }, [
    locked,
    name,
    next,
    usable,
    kind,
    setFieldValue,
    values,
    handle,
    takeovers,
    savedOverride,
  ]);

  // Said either way: locked, it is why the box is empty; unlocked, it is what
  // the recruiter has to correct before the offer will save.
  if (shown.unstorable) return <Refusal>{shown.unstorable}</Refusal>;
  // "This formula cannot be worked out" is what the engine answers when an
  // input is empty, and on its own it is a dead end — the recruiter is the one
  // who can fill that box. Where we know which field is missing, name it
  // instead (CORE-5487).
  //
  // Said on an optional field too. The rule used to be that an optional field
  // simply stays blank, but a diagnostic was rendered regardless of mandatory,
  // so the quieter message was never what happened: the filler got the dead
  // end either way. Naming the field is strictly better than that.
  // A refusal the filler cannot clear comes first: it is the admin's to fix,
  // and telling the recruiter to fill a box would send them nowhere.
  if (refusal && !isMissingInput(refusal))
    return <Refusal>{describeDiagnostic(refusal)}</Refusal>;
  if (!live && waitingFor && (blocked || isMissingInput(refusal)))
    return <Refusal>{waitingMessage(fillable, offForm)}</Refusal>;
  // Nothing to work with and nothing to name: the generic line is all there is.
  if (refusal) return <Refusal>{describeDiagnostic(refusal)}</Refusal>;
  // The formula ran and answered with nothing on a field the admin made
  // mandatory. Nothing above has anything to say about it, and the field's own
  // message is off, so the save would refuse without a word.
  //
  // Held back until the recruiter tries to send: the others explain why a box
  // is blank, or name something an admin has to fix, and are worth saying on
  // sight. This one only complains, and on open it complained about a field
  // nobody had touched yet (Venil).
  if (submitCount > 0 && required && locked && !usable)
    return (
      <Refusal>
        This field is required, but its formula has not produced a value yet.
      </Refusal>
    );
  return null;
};
